import { useEffect, useState } from "react";
import cookieConsentService from "../../services/cookieConsent";
import type { CookieConsentProvider } from "../../services/cookieConsent";

export interface CookieConsentState {
  /** Whether the consent banner is being shown for this visit. */
  readonly shouldShowBanner: boolean;
  /** Whether the user has consented to non-essential cookies. */
  readonly hasConsented: boolean;
}

/**
 * React hook wrapping the {@link CookieConsentProvider} service.
 *
 * On mount it suppresses the banner when the user has already responded
 * (e.g. accepted on the main `designmynight.com` site) and otherwise asks the
 * provider to show it. It then tracks consent changes so consumers can gate
 * behaviour (such as analytics) on the current decision.
 *
 * @param provider consent provider to use; defaults to the configured service.
 */
export function useCookieConsent(provider: CookieConsentProvider = cookieConsentService): CookieConsentState {
  const [hasConsented, setHasConsented] = useState<boolean>(() => provider.hasConsented());
  const [shouldShowBanner, setShouldShowBanner] = useState<boolean>(() => !provider.hasResponded());

  useEffect(() => {
    if (!provider.hasResponded()) {
      provider.showBanner();
    }

    const unsubscribe = provider.onConsentChange((consented) => {
      setHasConsented(consented);
      setShouldShowBanner(false);
    });

    return unsubscribe;
  }, [provider]);

  return { shouldShowBanner, hasConsented };
}

export default useCookieConsent;
